// Stores
import { useSearchResultsStore } from '@white-label-store/search-results';

// Types
import type {
  ParkingFormData,
  LoungeFormData,
  TravelInsuranceFormData
} from '@white-label-types/component';
import type {
  SearchProducts,
  productTypeId,
} from '@white-label-types/parking-booking';
import type { SearchCompletedData } from '@white-label-types/pusher';
import type { default as Pusher } from 'pusher-js';

// Packages
import { v4 as uuidv4 } from 'uuid';
import { createSearchCriteria } from '@white-label-helper/data-parsers-create-search-criteria';
import { buildSearchResults, buildSearchResultsForTerminalFlights } from '@white-label-helper/data-parsers-search-results';
import {
  PusherErrors,
  pusherErrorTimeout,
  FEATURE_FLAGS,
} from '@white-label-configuration/constants';
import getPusherChannel from '@white-label-helper/pusher-channel';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import {
  getProductSearchResults,
  searchProductAvailability,
} from '@white-label-helper/api-parkings-product';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { addCustomMetric, trackAction } from '@white-label-helper/shared-helpers';
import { TerminalFlight } from '@white-label-types/terminal-flight';
import { datadogRum } from "@datadog/browser-rum";
// Mocks
import { mockedTravelInsuranceResponse } from './mock-data/mocked-return-insurance-response';

let channel: Pusher | undefined;

type AdditionalFormData = {
  parking?: ParkingFormData;
  lounges?: LoungeFormData;
  fasttrack?: LoungeFormData;
}

export function searchPusherHelper() {
  let searchId: string;

  function destroyChannelListener() {
    if (channel === undefined) {
      channel = getPusherChannel();
    }

    channel.unsubscribe(searchId);
  }

  function fetchProductSearchResults(
    searchId: string,
    airportCode: string | undefined,
    productId: productTypeId
  ) {
    const store = useSearchResultsStore();
    const startTime = performance.now();
    const is_sync = getAppVariable('is_sync');
    return new Promise((resolve, reject) => {
      getProductSearchResults(searchId).then((response) => {
        addCustomMetric('tracking', 'Search Metric: Completed', {
          is_simple_search: String(is_sync),
          is_classic_search: String(!is_sync),
          event: 'SearchCompletedEvent',
          airport_code: airportCode,
        }, startTime, 'search_service', airportCode);

        store.updatePlaybooks(response.body?.data?.playbooks[productId]);
        if (response.body.data.search.inventory_metadata) {
          store.updateInventoryMetadata(response.body.data.search.inventory_metadata);
        }

        const results = response.body.data?.results;
        const productData = results?.[productId];
        const inventoryProvider = productData?.[0]?.inventory_provider;

        if (response.body.data.booking_fee) {
          store.updateBookingFee(response.body.data.booking_fee)
        }

        const { errors } = response.body.data;
        if (errors && !Array.isArray(errors) && Object.keys(errors).length) {
          store.updateSearchResultsErrors(response.body.data.errors);
        }

        if (productId == 'fasttrack' && inventoryProvider === "copenhagen-optimization" && Array.isArray(productData)) {
          resolve(buildSearchResultsForTerminalFlights(productData));
        } else {
          // If multiple product type results exist, store them in search results store
          const multiProductTypeKeys = Object.keys(results);
          if (multiProductTypeKeys.length) {
            multiProductTypeKeys.forEach((key) => {
              store.updateSearchResultsByProductType({
                searchResults: buildSearchResults(response.body.data.results[key], key as productTypeId),
                productType: key as productTypeId,
              });
            });

            if (getFeatureFlag(FEATURE_FLAGS.EM_228_TRAVEL_INSURANCE)) {
              store.updateSearchResultsByProductType({
                searchResults: buildSearchResults(mockedTravelInsuranceResponse.insurance, 'travel_insurance' as productTypeId),
                productType: 'travel_insurance' as productTypeId,
              });
            }
          }
          resolve(buildSearchResults(response.body.data?.results?.[productId], productId));
        }
    }).catch((error) => {
      console.error("Error fetching product search results:", error);
      reject(error);
    });
  });
}

  function searchProducts(
    formData: ParkingFormData | LoungeFormData | TravelInsuranceFormData,
    productId: productTypeId = 'parking',
    locale: string = '',
    additionalFormData?: AdditionalFormData,
  ) {
    const startTime = performance.now();
    const is_sync = getAppVariable('is_sync');
    const airportCode = formData.airport as string | undefined;

    return new Promise<SearchProducts[] | TerminalFlight[]>((resolve, reject) => {

      const store = useSearchResultsStore();
      searchId = uuidv4();
      store.updateDateTimeOfLastSearch(new Date());

      let language = getAppVariable('default_language');
      if (locale) {
        language = locale;
      } else {
        language = this.$i18n.locale;
      }

      const searchCriteria = createSearchCriteria(formData, searchId, productId, language);

      // If `additionalFormData` is defined, create search criteria for each additional product type
      if (additionalFormData !== undefined) {
        (Object.keys(additionalFormData) as (keyof AdditionalFormData)[]).forEach((key) => {
          if (additionalFormData[key] === undefined) return;
          searchCriteria.criteria[key] = createSearchCriteria(
            additionalFormData[key],
            searchId,
            key,
            language
          )['criteria'][key];
        });
      }

      store.updateSearchToken(searchId);

      if (channel === undefined) {
        channel = getPusherChannel();
      }

      // 1. Subscribe to pusher channel for this search
      const localPusher = channel.subscribe(`search.results.${searchId}`);

      // 2. Bind to `SearchCompletedEvent` for fetching and processing search results
      localPusher.bind(
        'SearchCompletedEvent',
        (eventData: SearchCompletedData) => {
          destroyChannelListener();
          const results = eventData?.results;
          const productsAvailable = getAppVariable<ProductType[]>('product_types_available', []);

          // If results in event data, use these
          // Update: this block is rarely used and can be confusing. Adding a custom attribute to track how often it is used
          // Potentially this can be removed in the future
          if (productsAvailable.length === 1 && Array.isArray(results) && results.length) {
            addCustomMetric('tracking',
              'Search Metric: Completed', {
              is_simple_search: String(is_sync),
              is_classic_search: String(!is_sync),
              event: 'SearchCompletedEvent',
              is_immediate_results: 'true' // custom attribute to track usage
            }, startTime, 'search_service', airportCode)

            if (eventData.playbooks) {
              store.updatePlaybooks(eventData.playbooks[productId]);
            }
            if (eventData.inventory_metadata) {
              store.updateInventoryMetadata(eventData.inventory_metadata);
            }
            resolve(buildSearchResults(results, productId));
            return;
          }

          // request API for results
          resolve(fetchProductSearchResults(searchId, airportCode, productId));
        }
      );

      // 3. Bind to `SearchProductFailedEvent` for handling search errors
      localPusher.bind('SearchProductFailedEvent', (error: { product_code: string; error: { response: string } }) => {
        // log failed search action with datadog
        trackAction('SearchProductFailedEvent', {
          productId,
          searchId,
        });
        if (!error.product_code || error.product_code === getAppVariable('primary_product_type')) {
          reject(new Error('SearchProductFailedEvent'));
        } else {
          addCustomMetric("error", "Search Metric: Secondary product error", {
            is_simple_search: String(is_sync),
            is_classic_search: String(!is_sync),
            failure_response: error.error?.response || "An error occurred",
          }, startTime, "search_service", airportCode);
        }
      });

      // Function for searching for product availability
      const searchProducts = () => {
        searchProductAvailability(searchCriteria)
          .then((response) => {
            trackAction('searchProductAvailabilitySuccess', {
              searchCriteria: JSON.stringify(searchCriteria.criteria),
              searchId: searchCriteria.token,
            });
            store.updatePlaybooks(response.body.data.playbooks[productId]);
            if (response.body.data.search.inventory_metadata) {
              store.updateInventoryMetadata(response.body.data.search.inventory_metadata);
            }

            if (response.body.data.booking_fee) {
              store.updateBookingFee(response.body.data.booking_fee)
            }

            const { errors } = response.body.data;
            if (errors && !Array.isArray(errors) && Object.keys(errors).length) {
              store.updateSearchResultsErrors(response.body.data.errors);
            }

            if (!response.body.data.is_more_coming) {
              // If multiple product type results exist, store them in search results store
              const multiProductTypeKeys = Object.keys(response.body.data?.results);
              if (multiProductTypeKeys.length) {
                multiProductTypeKeys.forEach((key) => {
                  store.updateSearchResultsByProductType({
                    searchResults: buildSearchResults(
                      response.body.data.results[key],
                      key as productTypeId
                    ),
                    productType: key as productTypeId,
                  })
                });
                if (getFeatureFlag(FEATURE_FLAGS.EM_228_TRAVEL_INSURANCE)) {
                  store.updateSearchResultsByProductType({
                    searchResults: buildSearchResults(mockedTravelInsuranceResponse.insurance, 'travel_insurance' as productTypeId),
                    productType: 'travel_insurance' as productTypeId,
                  });
                }
              }
              resolve(
                buildSearchResults(
                  response.body.data.results[productId],
                  productId
                )
              );
            }
          })
          .catch((error: { message: string }) => {
            datadogRum.addError(
              new Error('searchProductAvailability failed', { cause: error })
            );

            let data = null;
            try {
              data = JSON.parse(error.message);
            } catch (e) {
              console.log('possible error: ', e);
              datadogRum.addError('searchProductAvailability failed', {
                'search_service': {
                  ...data,
                }
              });
            }
            reject(
              Object.assign(new Error(PusherErrors.failedOnRequest), { data })
            );
          });
      };

      // Temporary feature flag check to fix a live issue where searches are completing before the connection
      // to pusher has succeeded. See ticket for more info - https://magusa.atlassian.net/browse/EM-1190
      const shouldWaitForPusherSubscription = getFeatureFlag(FEATURE_FLAGS.EM_1188_WAIT_FOR_PUSHER_CONNECTION);

      // 4. Trigger search request
      // If feature flag `EM_1188_WAIT_FOR_PUSHER_CONNECTION` is enabled, wait for the pusher subscription to succeed before searching products.
      // Otherwise, start searching immediately.
      if (shouldWaitForPusherSubscription) {
        localPusher.bind('pusher:subscription_succeeded', () => {
          searchProducts();
        });
      } else {
        searchProducts();
      }

      // Default timeout to reject request if it's frozen
      setTimeout(() => {
        reject(new Error(PusherErrors.failedOnPusherTimeout));
      }, pusherErrorTimeout);

    })
    .catch((e) => {
      addCustomMetric("error", "Search Metric: Exception", {
        is_simple_search: String(is_sync),
        is_classic_search: String(!is_sync),
        failure_response: e.message,
        search_id: searchId,
      }, startTime, "search_service", airportCode);

      throw e;
    });
  }

  function clearChannel() {
    channel = undefined;
  }

  return {
    searchProducts,
    destroyChannelListener,
    clearChannel
  };
}
